import { useMemo } from 'react'
import useApiService from 'services/api'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { getTargetType } from './targetTypeMap'
import {
  adaptUnifiedItemForVertical,
  adaptUnifiedListParams,
  VerticalRowConfig,
} from './unifiedResponseAdapter'

/**
 * Flag-gated data-source swap for an opportunities target table (FIR-6995).
 *
 * Returns `{ listAction, summaryAction }` bound to a vertical. When the
 * `enable_unified_opps_endpoint` flag is ON, the actions hit the unified
 * `/opportunities/` endpoint (resolving the vertical through `getTargetType`,
 * shaping outgoing params via `adaptUnifiedListParams`, and re-nesting each row
 * via `adaptUnifiedItemForVertical` so the legacy columns/links/actions resolve).
 * When OFF, the caller's existing legacy actions are returned untouched, so the
 * flag-off path is byte-for-byte unchanged.
 *
 * `DECommonTable` calls these as `(params)` (no target_type, no signal) — the
 * target_type is bound here and the adapter handles param/row shaping, so the
 * generic table needs no knowledge of the unified contract.
 *
 * @param vertical app vertical / sub-category slug (routed through `getTargetType`)
 * @param rowConfig how to re-nest the flat unified row for this vertical
 * @param legacy the existing legacy list/summary actions (used when flag is off)
 */
export function useUnifiedTableActions<T extends { id: number }>(
  vertical: string,
  rowConfig: VerticalRowConfig,
  legacy: {
    listAction: (
      params: Record<string, any>,
      signal?: AbortSignal
    ) => Promise<{ results: T[] }>
    summaryAction: (
      params: Record<string, any>,
      signal?: AbortSignal
    ) => Promise<{ count: number; alt_count: number }>
  }
): {
  listAction: (
    params: Record<string, any>,
    signal?: AbortSignal
  ) => Promise<{ results: T[] }>
  summaryAction: (
    params: Record<string, any>,
    signal?: AbortSignal
  ) => Promise<{ count: number; alt_count: number }>
} {
  const api = useApiService()
  const { enableUnifiedOppsEndpoint } = useFeatureFlagService()

  // Stable scalar key for the array-valued `companyIdAliases` dep below.
  const companyIdAliasesKey = rowConfig.companyIdAliases?.join(',') ?? ''

  return useMemo(() => {
    if (!enableUnifiedOppsEndpoint) {
      return legacy
    }

    const targetType = getTargetType(vertical)

    const listAction = async (
      params: Record<string, any>,
      signal?: AbortSignal
    ) => {
      const res = await api.getOpportunitiesList(
        targetType,
        adaptUnifiedListParams(params, targetType),
        signal
      )
      return {
        ...res,
        results: res.results.map((item) =>
          adaptUnifiedItemForVertical(
            item as Record<string, unknown>,
            rowConfig
          )
        ),
      } as unknown as { results: T[] }
    }

    const summaryAction = async (
      params: Record<string, any>,
      signal?: AbortSignal
    ) => {
      const res = await api.getOpportunitiesSummary(
        targetType,
        adaptUnifiedListParams(params, targetType),
        signal
      )
      // Unified summary returns `{ count }` (+ optional `alt_count`). Most
      // verticals only surface `count`; default `alt_count` so the shared
      // pagination/stat plumbing never reads `undefined`.
      return { count: res.count, alt_count: res.alt_count ?? 0 }
    }

    return { listAction, summaryAction }
    // `legacy` action identities are stable across renders (api methods); we key
    // the swap on the flag + vertical + row config.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enableUnifiedOppsEndpoint,
    vertical,
    api,
    rowConfig.nestKey,
    rowConfig.detailLinkIdField,
    companyIdAliasesKey,
  ])
}
